import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section, StatCell } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, ClockClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const BREACH_TYPES = ["document_missing", "late_marks", "policy_violation", "attendance_irregular", "other"];
const SEVERITY = ["low", "medium", "high"];
const STATUSES = ["open", "in_progress", "resolved", "dismissed"];

const empty = {
  employee_id: "",
  breach_type: "document_missing",
  severity: "medium",
  description: "",
  remarks: "",
  action_taken: "",
  status: "open",
};

export default function Compliance() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);

  const load = async () => {
    if (!activeCompanyId) return;
    const [b, e] = await Promise.all([
      api.get("/compliance", { params: { company_id: activeCompanyId } }),
      api.get("/employees", { params: { company_id: activeCompanyId, limit: 500 } }),
    ]);
    setItems(b.data);
    setEmployees(e.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const save = async () => {
    if (!form.description || form.description.trim().length < 3) {
      toast.error("Description is required (min 3 characters)");
      return;
    }
    try {
      const body = {
        ...form,
        company_id: activeCompanyId,
        employee_id: form.employee_id || null,
      };
      if (editingId) {
        await api.put(`/compliance/${editingId}`, body);
        toast.success("Breach updated");
      } else {
        await api.post("/compliance", body);
        toast.success("Breach logged");
      }
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const edit = (b) => {
    setForm({
      employee_id: b.employee_id || "",
      breach_type: b.breach_type,
      severity: b.severity,
      description: b.description,
      remarks: b.remarks || "",
      action_taken: b.action_taken || "",
      status: b.status,
    });
    setEditingId(b.id);
    setOpen(true);
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const counts = {
    open: items.filter((x) => x.status === "open").length,
    in_progress: items.filter((x) => x.status === "in_progress").length,
    resolved: items.filter((x) => x.status === "resolved").length,
    dismissed: items.filter((x) => x.status === "dismissed").length,
  };
  const severityColor = (s) => s === "high" ? "text-[#F54236]" : s === "medium" ? "text-[#FAB005]" : "text-[#64748B]";
  const statusColor = (s) => s === "resolved" ? "text-[#12B886]" : s === "open" ? "text-[#F54236]" : s === "in_progress" ? "text-[#FAB005]" : "text-[#64748B]";

  return (
    <div data-testid="compliance-page">
      <PageHeader eyebrow={activeCompany?.code} title="Compliance breaches" subtitle="Manual log of compliance issues. Payroll impact, if any, is added manually by HR.">
        {canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="add-breach-btn">
                <Plus size={14} className="mr-1.5" /> Log breach
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-xl">
              <DialogHeader><DialogTitle>{editingId ? "Update breach" : "Log compliance breach"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase">Employee (optional)</Label>
                  <Select value={form.employee_id || "__none__"} onValueChange={(v) => setForm({...form, employee_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger className="rounded-sm" data-testid="breach-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Company-wide (no specific employee)</SelectItem>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase">Type</Label>
                  <Select value={form.breach_type} onValueChange={(v) => setForm({...form, breach_type: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="breach-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{BREACH_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase">Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="breach-severity"><SelectValue /></SelectTrigger>
                    <SelectContent>{SEVERITY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {editingId && (
                  <div className="col-span-2">
                    <Label className="text-xs uppercase">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                      <SelectTrigger className="rounded-sm" data-testid="breach-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-xs uppercase">Description <span className="text-[#F54236]">*</span></Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-sm" data-testid="breach-description" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase">Action taken</Label>
                  <Textarea rows={2} value={form.action_taken} onChange={(e) => setForm({...form, action_taken: e.target.value})} className="rounded-sm" data-testid="breach-action" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase">Remarks</Label>
                  <Input value={form.remarks} onChange={(e) => setForm({...form, remarks: e.target.value})} className="rounded-sm" data-testid="breach-remarks" />
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="save-breach">{editingId ? "Update" : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      <PageBody className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell label="Open" value={counts.open} accent="text-[#F54236]" testId="breach-open" />
          <StatCell label="In progress" value={counts.in_progress} accent="text-[#FAB005]" />
          <StatCell label="Resolved" value={counts.resolved} accent="text-[#12B886]" />
          <StatCell label="Dismissed" value={counts.dismissed} />
        </div>

        <Section title={`${items.length} breach records`} dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Employee</TableHead>
              <TableHead>Severity</TableHead><TableHead>Description</TableHead>
              <TableHead>Status</TableHead>{canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#64748B] py-8">No compliance breaches logged</TableCell></TableRow>}
              {items.map((b) => (
                <TableRow key={b.id} data-testid={`breach-row-${b.id}`}>
                  <TableCell className="font-mono text-xs">{(b.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-xs uppercase tracking-wider">{b.breach_type.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs">
                    {b.employee_id ? (
                      <>
                        <div>{empMap[b.employee_id]?.full_name || "—"}</div>
                        <div className="font-mono text-[10px] text-[#64748B]">{empMap[b.employee_id]?.employee_code}</div>
                      </>
                    ) : <span className="text-[10px] uppercase text-[#64748B]">Company-wide</span>}
                  </TableCell>
                  <TableCell><span className={`text-[10px] uppercase font-semibold ${severityColor(b.severity)}`}><Warning size={10} className="inline mr-0.5" />{b.severity}</span></TableCell>
                  <TableCell className="text-xs max-w-[260px] truncate" title={b.description}>{b.description}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase font-semibold ${statusColor(b.status)}`}>{b.status.replace(/_/g, " ")}</span></TableCell>
                  {canEdit && (
                    <TableCell className="text-right space-x-1">
                      <button onClick={() => setHistoryOf(b)} className="p-1 hover:bg-[#EFF6FF] rounded-sm" title="History" data-testid={`history-${b.id}`}><ClockClockwise size={14}/></button>
                      <button onClick={() => edit(b)} className="text-[10px] uppercase tracking-wider text-[#2563EB] hover:underline" data-testid={`edit-${b.id}`}>Edit</button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      </PageBody>

      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>Breach history</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(historyOf?.history || []).map((h, i) => (
              <div key={i} className="border border-border rounded-sm p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold uppercase text-[10px] tracking-wider">{h.status}</span>
                  <span className="font-mono text-[10px] text-[#64748B]">{(h.at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="text-[#475569] mt-1">{h.remarks || "—"}</div>
                <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">{h.by_email}</div>
              </div>
            ))}
            {(historyOf?.history || []).length === 0 && <div className="text-xs text-[#64748B]">No history yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
